import React, { useRef, useEffect } from "react";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaSave,
  FaTimes,
  FaUndoAlt,
  FaRedoAlt,
} from "react-icons/fa";
import { FaLink } from "react-icons/fa6";
import { RiH1, RiH2, RiH3 } from "react-icons/ri";
import { MdFormatListBulleted, MdFormatListNumbered } from "react-icons/md";
import "./ContentEditor.css";

export default function ContentEditor({ initialContent, onSave, onClose }) {
  const editorRef = useRef(null);
  let savedRange = useRef(null);
  const undoAction = () => {
    restoreSelection();
    document.execCommand("undo");
  };

  const redoAction = () => {
    restoreSelection();
    document.execCommand("redo");
  };

  // ---------------- INIT ----------------
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialContent || "";
    }
  }, [initialContent]);

  // ---------------- SAVE SELECTION ----------------
  const saveSelection = () => {
    const sel = window.getSelection();

    if (sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0);
    }
  };

  // ---------------- RESTORE SELECTION ----------------
  const restoreSelection = () => {
    const sel = window.getSelection();
    sel.removeAllRanges();

    if (savedRange.current) {
      sel.addRange(savedRange.current);
    }
  };

  // ---------------- BASIC FORMAT ----------------
  const applyFormat = (cmd) => {
    restoreSelection();
    document.execCommand(cmd, false, null);
  };

  // ---------------- HEADING APPLY ----------------
  const applyHeading = (tag) => {
    restoreSelection();
    document.execCommand("formatBlock", false, tag);
  };

  // ---------------- APPLY LINK ----------------
  const applyLink = () => {
    restoreSelection();

    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const selectedText = sel.toString().trim();

    if (!selectedText) return;

    let url = selectedText;
    if (!url.startsWith("http")) url = "https://" + url;

    document.execCommand("createLink", false, url);

    setTimeout(() => {
      let anchor = sel.anchorNode?.parentElement;

      if (anchor && anchor.tagName === "A") {
        anchor.style.color = "blue";
      }
    }, 20);
  };

  // ---------------- LISTS ----------------
  const applyList = (ordered = false) => {
    restoreSelection();
    document.execCommand(ordered ? "insertOrderedList" : "insertUnorderedList");
  };

  // ---------------- AUTO-DETECT URL ----------------
  const autoDetectLinks = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const node = sel.anchorNode;

    if (!node || node.nodeType !== 3) return;

    const text = node.textContent;

    const urlRegex =
      /(https?:\/\/[^\s<]+|www\.[^\s<]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,})/;

    if (!urlRegex.test(text)) {
      return;
    }

    const match = text.match(urlRegex)[0];

    const url = match.startsWith("http") ? match : "https://" + match;

    const link = document.createElement("a");
    link.href = url;
    link.textContent = match;
    link.style.color = "blue";

    const before = text.split(match)[0];
    const after = text.split(match)[1];

    const parent = node.parentNode;
    parent.insertBefore(document.createTextNode(before), node);
    parent.insertBefore(link, node);
    parent.insertBefore(document.createTextNode(after), node);
    parent.removeChild(node);
  };

  // ---------------- AUTO-DETECT LISTS ----------------
  const autoDetectLists = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const node = sel.anchorNode;
    if (!node || node.nodeType !== 3) return;

    let text = node.textContent;

    // Normalize NBSP → normal space
    const normalized = text.replace(/\u00A0/g, " ");

    // Auto bullet: "- "  OR  "- "
    if (normalized.endsWith("- ")) {
      node.textContent = "";
      document.execCommand("insertUnorderedList");
      return;
    }

    // Auto number: "1. "
    if (/^\d+\.\s$/.test(normalized)) {
      node.textContent = "";
      document.execCommand("insertOrderedList");
      return;
    }

    // Auto number: "1 "
    if (/^\d+\s$/.test(normalized)) {
      node.textContent = "";
      document.execCommand("insertOrderedList");
      return;
    }
  };

  // ---------------- MAIN INPUT HANDLER ----------------
  const handleInput = () => {
    autoDetectLinks();
    autoDetectLists();
    saveSelection();
  };

  return (
    <div className="ct-editor-overlay">
      <div className="ct-editor-modal">
        {/* HEADER */}
        <div className="ct-editor-header">
          {/* LEFT ICONS */}
          <div className="left-icons">
            <FaBold className="icon" onClick={() => applyFormat("bold")} />
            <FaItalic className="icon" onClick={() => applyFormat("italic")} />
            <FaUnderline
              className="icon"
              onClick={() => applyFormat("underline")}
            />
            <FaLink className="icon" onClick={applyLink} />

            <RiH1 className="icon" onClick={() => applyHeading("h1")} />
            <RiH2 className="icon" onClick={() => applyHeading("h2")} />
            <RiH3 className="icon" onClick={() => applyHeading("h3")} />

            <MdFormatListBulleted
              className="icon"
              onClick={() => applyList(false)}
            />
            <MdFormatListNumbered
              className="icon"
              onClick={() => applyList(true)}
            />
            <FaUndoAlt className="icon" onClick={undoAction} />
            <FaRedoAlt className="icon" onClick={redoAction} />
          </div>

          {/* RIGHT BUTTONS */}
          <div className="right-buttons">
            <button
              className="save-btn"
              onClick={() => onSave(editorRef.current.innerHTML)}
            >
              <FaSave /> Save
            </button>
            <button className="close-btn" onClick={onClose}>
              <FaTimes /> Close
            </button>
          </div>
        </div>

        {/* EDITOR */}
        <div
          className="ct-editor-box"
          contentEditable
          ref={editorRef}
          onInput={handleInput}
          onMouseUp={saveSelection}
          onKeyUp={saveSelection}
          onMouseDown={saveSelection}
          suppressContentEditableWarning={true}
        ></div>
      </div>
    </div>
  );
}
